import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { QuoteApprovalView } from "../pages/QuoteApprovalPage";
import { FileText, EnvelopeSimple, ArrowClockwise, PaperPlaneTilt, FilePdf } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

function PdfTab({ clientId, draft, active }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    setUrl(""); setErr("");
    API.post(`/clients/${clientId}/branding/preview/pdf`, { draft }, { responseType: "blob" })
      .then((res) => { if (alive) setUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))); })
      .catch(() => alive && setErr("Could not render the PDF preview."));
    return () => { alive = false; };
  }, [active, clientId, JSON.stringify(draft)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (err) return <p className="p-6 text-sm text-destructive" data-testid="brand-preview-pdf-error">{err}</p>;
  if (!url) return <p className="p-6 text-sm text-muted-foreground" data-testid="brand-preview-pdf-loading">Rendering PDF…</p>;
  return <iframe title="Quote PDF preview" src={`${url}#toolbar=0&view=FitH`} className="flex-1 min-h-[520px] w-full rounded-md border border-border bg-white" data-testid="brand-preview-pdf-frame" />;
}

/* Admin: what a client's users will actually see — the branded public quote page, the "quote is ready" email and the client PDF.
   `draft` = the Portal Branding form as currently edited (name/tagline/footer/colour) so unsaved changes preview live. */
export default function BrandPreviewDialog({ clientId, open, onOpenChange, clientName = "", draft = null, dirty = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("quote");
  const { user } = useAuth();
  const [toEmail, setToEmail] = useState(user?.email || "");
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(toEmail.trim());

  const sendTest = () => {
    if (!validEmail) { toast.error("Enter a valid email address"); return; }
    setSending(true);
    API.post(`/clients/${clientId}/branding/preview/send-test`, { to_email: toEmail.trim() })
      .then(({ data }) => toast.success(`Preview copy sent to ${data.to_email}`, { description: `From "${data.from_name}" · ${data.subject}` }))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Could not send the test email"))
      .finally(() => setSending(false));
  };

  const load = () => {
    setLoading(true); setError("");
    (draft ? API.post(`/clients/${clientId}/branding/preview`, { draft }) : API.get(`/clients/${clientId}/branding/preview`))
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Could not build the preview"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { setTab("quote"); load(); } }, [open, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const brand = data?.quote?.branding || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="brand-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <div className="flex items-start justify-between gap-4 pr-6">
            <div>
              <DialogTitle className="font-[Chivo] text-base">Brand preview{clientName ? ` — ${clientName}` : ""}</DialogTitle>
              <DialogDescription className="text-xs">
                Exactly what this client's users see. {data?.source === "latest_quote" ? <>Using their latest quote <span className="font-mono">{data.quote_reference}</span>.</> : data ? "No quotes yet — showing a sample quote." : ""} Nothing is sent to the client from here.
              </DialogDescription>
              {dirty && (
                <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2.5 py-0.5 text-[11px] font-semibold" data-testid="brand-preview-unsaved">
                  Previewing unsaved changes — save the branding to apply them
                </p>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="brand-preview-refresh"><ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /></Button>
          </div>
        </DialogHeader>
        {error ? (
          <p className="p-6 text-sm text-destructive" data-testid="brand-preview-error">{error}</p>
        ) : !data ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="brand-preview-loading">Building preview…</p>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-6 mt-3 w-fit">
              <TabsTrigger value="quote" data-testid="brand-preview-tab-quote"><FileText size={14} className="mr-1.5" /> Portal quote page</TabsTrigger>
              <TabsTrigger value="email" data-testid="brand-preview-tab-email"><EnvelopeSimple size={14} className="mr-1.5" /> Email</TabsTrigger>
              <TabsTrigger value="pdf" data-testid="brand-preview-tab-pdf"><FilePdf size={14} className="mr-1.5" /> PDF</TabsTrigger>
            </TabsList>
            <TabsContent value="quote" className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 mt-3" data-testid="brand-preview-quote">
              <QuoteApprovalView quote={data.quote} preview onRespond={() => {}} />
            </TabsContent>
            <TabsContent value="email" className="flex-1 min-h-0 flex flex-col px-6 pb-6 mt-3" data-testid="brand-preview-email">
              <div className="rounded-md border border-border bg-muted/30 px-4 py-2.5 text-xs mb-3 space-y-2">
                <div className="space-y-1 min-w-0">
                  <p><span className="text-muted-foreground w-16 inline-block">From</span><span className="font-medium" data-testid="brand-preview-email-from">{data.email.from_name || brand.company_name || "—"}</span></p>
                  <p><span className="text-muted-foreground w-16 inline-block">Subject</span><span className="font-medium" data-testid="brand-preview-email-subject">{data.email.subject}</span></p>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60">
                  <span className="text-muted-foreground">Send a preview copy to</span>
                  <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendTest(); }} placeholder="name@example.com" className="h-8 text-xs w-64" data-testid="brand-preview-send-to-input" />
                  <Button size="sm" variant="outline" className="h-8" onClick={sendTest} disabled={sending || !validEmail} data-testid="brand-preview-send-test-btn">
                    <PaperPlaneTilt size={14} className="mr-1.5" /> {sending ? "Sending…" : "Send preview"}
                  </Button>
                  {user?.email && <button type="button" onClick={() => setToEmail(user.email)} className="rounded-full border border-border px-2 py-0.5 text-[11px] hover:bg-accent" data-testid="brand-preview-send-to-me">Me · {user.email}</button>}
                  {data.client_email && data.client_email !== user?.email && <button type="button" onClick={() => setToEmail(data.client_email)} className="rounded-full border border-border px-2 py-0.5 text-[11px] hover:bg-accent" data-testid="brand-preview-send-to-client">{data.client_contact ? `${data.client_contact} · ` : "Client contact · "}{data.client_email}</button>}
                </div>
                <p className="text-[11px] text-muted-foreground">Marked "[Preview]" with a review banner — the approve button in the copy is not a live link.</p>
              </div>
              <iframe title="Email preview" sandbox="" srcDoc={data.email.html} className="flex-1 min-h-[420px] w-full rounded-md border border-border bg-white" data-testid="brand-preview-email-frame" />
            </TabsContent>
            <TabsContent value="pdf" className="flex-1 min-h-0 flex flex-col px-6 pb-6 mt-3" data-testid="brand-preview-pdf">
              <p className="text-[11px] text-muted-foreground mb-2">The client-view quote PDF in this brand — logo, name, footer and accent colour.</p>
              <PdfTab clientId={clientId} draft={draft} active={tab === "pdf"} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
